/**
 * Base Display Mode Strategy
 *
 * Abstract base class for display mode strategies that provides
 * idempotent scroll lock management. Strategies that need scroll lock
 * inherit the default behavior; strategies that don't (like banner)
 * override the methods.
 *
 * The hasScrollLock guard prevents double-acquire bugs that can occur
 * when applyBodyState() is called multiple times (e.g., once in
 * revealPopupWithLoader and again in revealPopup).
 */

import type { DisplayConfig } from '../../shared/types/popup';
import type { DisplayModeStrategy } from './types';
import { ScrollLockManager } from '../utils/scroll-lock-manager';

export abstract class BaseDisplayModeStrategy implements DisplayModeStrategy {
	protected hasScrollLock = false;

	/**
	 * Returns CSS class(es) for this display mode.
	 * Must be implemented by each strategy.
	 */
	abstract getDisplayModeClasses(): string[];

	/**
	 * Whether backdrop click should close the popup.
	 * Must be implemented by each strategy.
	 */
	abstract shouldCloseOnBackdropClick(config: DisplayConfig): boolean;

	/**
	 * Apply body modifications when popup opens.
	 * Default: acquire scroll lock (idempotent - safe to call multiple times).
	 * Override in strategies that don't need scroll lock (e.g., banner).
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public applyBodyState(config?: DisplayConfig): void {
		if (!this.hasScrollLock) {
			ScrollLockManager.acquire();
			this.hasScrollLock = true;
		}
	}

	/**
	 * Remove body modifications when popup closes.
	 * Default: release scroll lock (only if acquired).
	 * Override in strategies that don't need scroll lock (e.g., banner).
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public removeBodyState(config?: DisplayConfig): void {
		if (this.hasScrollLock) {
			ScrollLockManager.release();
			this.hasScrollLock = false;
		}
	}
}
